import { Controller } from "@hotwired/stimulus"

// Tabs com persistência via URL hash (WAI-ARIA APG Tabs pattern).
//
// - Estado vive em `window.location.hash` (#forms, #display, etc.).
// - F5 mantém a aba ativa porque o hash sobrevive ao reload.
// - Botão voltar/avançar do browser navega entre abas (popstate).
// - Deep-link: `/admin/components#forms` abre direto em Forms.
// - Suporta hash com âncora de seção (`#forms-select`): a parte antes do `-`
//   ativa a tab, o resto é tratado como id para scroll-into-view.
//
// A11y:
//   - Roving tabindex: só a tab ativa tem tabindex=0, demais tabindex=-1
//   - ←/→/Home/End navegam entre tabs (focus + auto-select)
//   - aria-selected sincronizado
//   - aria-controls aponta o painel correspondente
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values  = { active: String }

  connect() {
    this._onHashChange = this._onHashChange.bind(this)
    this._onTabKeydown = this._onTabKeydown.bind(this)
    window.addEventListener("hashchange", this._onHashChange)
    this.tabTargets.forEach((t) => t.addEventListener("keydown", this._onTabKeydown))
    this._applyFromHash({ scroll: true })
  }

  disconnect() {
    window.removeEventListener("hashchange", this._onHashChange)
    this.tabTargets.forEach((t) => t.removeEventListener("keydown", this._onTabKeydown))
  }

  select(e) {
    const key = e.currentTarget.dataset.key
    this.activeValue = key
    // replaceState para não poluir o histórico com cada clique de tab.
    history.replaceState(null, "", `#${key}`)
    this.render()
  }

  _onHashChange() {
    this._applyFromHash({ scroll: true })
  }

  _onTabKeydown(e) {
    if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) return
    e.preventDefault()
    const tabs = this.tabTargets
    const current = tabs.indexOf(document.activeElement)
    let next = current

    switch (e.key) {
      case "ArrowRight": next = (current + 1) % tabs.length; break
      case "ArrowLeft":  next = current <= 0 ? tabs.length - 1 : current - 1; break
      case "Home":       next = 0; break
      case "End":        next = tabs.length - 1; break
    }
    const target = tabs[next]
    this.activeValue = target.dataset.key
    history.replaceState(null, "", `#${target.dataset.key}`)
    this.render()
    target.focus()
  }

  _applyFromHash({ scroll = false } = {}) {
    const raw = window.location.hash.replace(/^#/, "")
    if (!raw) {
      this.render()
      return
    }
    const keys      = this.tabTargets.map((t) => t.dataset.key)
    const tabKey    = keys.find((k) => raw === k || raw.startsWith(`${k}-`))
    if (tabKey) {
      this.activeValue = tabKey
    }
    this.render()
    if (scroll && raw !== tabKey) {
      requestAnimationFrame(() => {
        const el = document.getElementById(raw)
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }
  }

  render() {
    this.tabTargets.forEach((t) => {
      const active = t.dataset.key === this.activeValue
      t.toggleAttribute("data-active", active)
      t.setAttribute("aria-selected", active)
      t.setAttribute("tabindex", active ? "0" : "-1")
      // aria-current reforça a localização atual pra tecnologias assistivas que
      // o usam como dica de navegação (complementa aria-selected).
      if (active) t.setAttribute("aria-current", "page")
      else t.removeAttribute("aria-current")
    })
    this.panelTargets.forEach((p) => {
      p.toggleAttribute("hidden", p.dataset.key !== this.activeValue)
    })
  }
}
